import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { erc20Abi, type Address } from "viem";
import { keelVaultAbi, keelVaultFactoryAbi } from "../abi";
import { implDeClon } from "../lib/clon";
import { cantidades } from "../lib/precio";
import { esIgual, type RedKeel } from "../config/networks";
import { clientePublico } from "../lib/cliente";

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  { type: "function", name: "tickSpacing", stateMutability: "view", inputs: [], outputs: [{ type: "int24" }] },
] as const;

const NPM_ABI = [
  { type: "function", name: "positions", stateMutability: "view", inputs: [{ type: "uint256" }],
    outputs: [{ type: "uint96" }, { type: "address" }, { type: "address" }, { type: "address" },
              { type: "uint24" }, { type: "int24" }, { type: "int24" }, { type: "uint128" },
              { type: "uint256" }, { type: "uint256" }, { type: "uint128" }, { type: "uint128" }] },
  { type: "function", name: "collect", stateMutability: "payable",
    inputs: [{ type: "tuple", components: [
      { name: "tokenId", type: "uint256" }, { name: "recipient", type: "address" },
      { name: "amount0Max", type: "uint128" }, { name: "amount1Max", type: "uint128" }] }],
    outputs: [{ type: "uint256" }, { type: "uint256" }] },
] as const;

const MAX128 = (1n << 128n) - 1n;

export type EstadoVault = {
  direccion: Address;
  dueno: Address;
  esMio: boolean;
  pool: Address;
  /** el NonfungiblePositionManager de Uniswap: hace falta para enlazar al NFT */
  npm: Address;
  token0: { address: Address; symbol: string; decimals: number };
  token1: { address: Address; symbol: string; decimals: number };
  refEsToken0: boolean;
  /** el token de referencia: el estable del par */
  ref: { symbol: string; decimals: number };

  tokenId: bigint;
  abierto: boolean;
  rango: { lo: number; hi: number } | null;
  enRango: boolean;
  tick: number;
  /** token1 por token0, en unidades humanas */
  precio: number;
  spacing: number;

  /** lo que hay dentro de la posicion, humano */
  enPosicion: { a0: number; a1: number };
  /** liquidez de la posicion (la L de Uniswap, en bruto). 0 si no hay posicion abierta.
   *  Con ella y los dos bordes se sabe en que se convierte la posicion a cualquier precio. */
  liquidez: number;
  /** suelto en el vault, sin descontar nada */
  libre: { a0: number; a1: number };

  costBasisRef: bigint;
  capitalHoy: number;
  objetivo: number;
  /** capitalHoy - objetivo, en el token de referencia */
  diferencia: number;

  profit0: bigint;
  profit1: bigint;
  /** comisiones que Uniswap todavia retiene: hay que SIMULAR collect, `tokensOwed` no vale */
  pendientes: { a0: number; a1: number };

  gasBudget0: bigint;
  gasBudget1: bigint;
  reserva: bigint;
  reserveInitial: bigint;

  halfWidthTicks: number;
  /** semianchos por lado. 0/0 = rango simétrico `±halfWidthTicks` (legado y modo "±%"). */
  ticksBelow: number;
  ticksAbove: number;
  /** el vault tiene el rango asimétrico (impl nueva). Los viejos no exponen estas funciones. */
  asimSoportado: boolean;
  twapWindow: number;
  maxTwapDeviationTicks: number;
  swapSlippageBps: number;
  cooldown: number;
  lastRebalance: number;
  cuota: number;
  pausado: boolean;
  operador: Address;
  reinvertir: boolean;

  ilRecover: boolean;
  ilRecoverMaxTicks: number;
  minSaleOn: boolean;
  minSaleMaxBps: number;
  /** colchon del rango de recuperacion, en bps sobre el capital. 0 = apuntar al capital pelado */
  recoverMarginBps: number;
  /** Colchón FIJO en unidades del token de referencia (0 = el vault sigue con el colchón en %). */
  recoverMarginRef: bigint;
  colchonFijoSoportado: boolean;
  /** los vaults anteriores no tienen el colchon: no hay que enseñarles la casilla */
  colchonSoportado: boolean;

  reinjectMode: number;
  reinjectPctBps: number;
  dropStepBps: number;
  dropStepsDone: number;

  rebalancer: Address;
  impl?: Address;
  /** hay una logica mas nueva publicada por su factory */
  hayActualizacion: boolean;
  logicaPublicada: Address;
};

export function useVault(direccion: Address | null, red: RedKeel | undefined) {
  const { address } = useAccount();

  return useQuery({
    queryKey: ["vault", red?.clave, direccion, address],
    enabled: !!direccion && !!red,
    refetchInterval: 30_000,
    queryFn: async (): Promise<EstadoVault> => {
      const client = clientePublico(red!.idCadena);
      if (!client) throw new Error("sin cliente RPC");
      const v = direccion!;
      const comun = { address: v, abi: keelVaultAbi } as const;

      const [
        dueno, pool, npm, t0, t1, tokenId, costBasisRef, recoverToken0,
        profit0, profit1, gb0, gb1, reserve, reserveInitial,
        halfWidthTicks, twapWindow, maxTwapDeviationTicks, swapSlippageBps,
        cooldown, lastRebalance, cuota, paused, operador, reinvestFees,
        ilRecover, ilRecoverMaxTicks, minSaleOn, minSaleMaxBps,
        reinjectMode, reinjectPctBps, dropStepBps, dropStepsDone, rebalancer, factory,
      ] = await client.multicall({
        allowFailure: false,
        contracts: [
          { ...comun, functionName: "owner" }, { ...comun, functionName: "pool" },
          { ...comun, functionName: "npm" }, { ...comun, functionName: "token0" },
          { ...comun, functionName: "token1" }, { ...comun, functionName: "tokenId" },
          { ...comun, functionName: "costBasisRef" }, { ...comun, functionName: "recoverToken0" },
          { ...comun, functionName: "profit0" }, { ...comun, functionName: "profit1" },
          { ...comun, functionName: "gasBudget0" }, { ...comun, functionName: "gasBudget" },
          { ...comun, functionName: "reserve" }, { ...comun, functionName: "reserveInitial" },
          { ...comun, functionName: "halfWidthTicks" }, { ...comun, functionName: "twapWindow" },
          { ...comun, functionName: "maxTwapDeviationTicks" }, { ...comun, functionName: "swapSlippageBps" },
          { ...comun, functionName: "cooldown" }, { ...comun, functionName: "lastRebalance" },
          { ...comun, functionName: "rebalancesRemaining" }, { ...comun, functionName: "paused" },
          { ...comun, functionName: "operator" }, { ...comun, functionName: "reinvestFees" },
          { ...comun, functionName: "ilRecover" }, { ...comun, functionName: "ilRecoverMaxTicks" },
          { ...comun, functionName: "minSaleOn" }, { ...comun, functionName: "minSaleMaxBps" },
          { ...comun, functionName: "reinjectMode" }, { ...comun, functionName: "reinjectPctBps" },
          { ...comun, functionName: "dropStepBps" }, { ...comun, functionName: "dropStepsDone" },
          { ...comun, functionName: "rebalancer" }, { ...comun, functionName: "factory" },
        ],
      });

      // EL COLCHÓN SE LEE APARTE, Y A PROPÓSITO. El multicall de arriba va con
      // `allowFailure: false`, así que una función que el vault no tiene tumbaría la lectura
      // entera: los vaults con la implementación anterior dejarían de verse. Aquí la reversión ES
      // la respuesta.
      let recoverMarginBps = 0;
      let colchonSoportado = true;
      try {
        recoverMarginBps = Number(await client.readContract({ ...comun, functionName: "recoverMarginBps" }));
      } catch {
        colchonSoportado = false;
      }

      // El colchón FIJO (importe en el token de referencia) sustituye al porcentual. Se lee aparte
      // por lo mismo: los vaults abiertos con la implementación anterior no tienen esta ranura y
      // seguirán mostrando su colchón en %. 0 = ese vault sigue con el porcentaje.
      let recoverMarginRef = 0n;
      let colchonFijoSoportado = true;
      try {
        recoverMarginRef = (await client.readContract({ ...comun, functionName: "recoverMarginRef" })) as bigint;
      } catch {
        colchonFijoSoportado = false;
      }

      // El rango asimétrico se lee aparte por la misma razón que el colchón: los vaults con la
      // implementación anterior no tienen estas funciones y tumbarían el multicall `allowFailure:
      // false`. 0/0 = simétrico (`±halfWidthTicks`); la reversión ES la respuesta para los viejos.
      let ticksBelow = 0, ticksAbove = 0, asimSoportado = true;
      try {
        const [tb, ta] = await client.multicall({
          allowFailure: false,
          contracts: [{ ...comun, functionName: "ticksBelow" }, { ...comun, functionName: "ticksAbove" }],
        });
        ticksBelow = Number(tb);
        ticksAbove = Number(ta);
      } catch {
        asimSoportado = false;
      }

      const meta = await client.multicall({
        allowFailure: false,
        contracts: [
          { address: t0, abi: erc20Abi, functionName: "symbol" },
          { address: t0, abi: erc20Abi, functionName: "decimals" },
          { address: t1, abi: erc20Abi, functionName: "symbol" },
          { address: t1, abi: erc20Abi, functionName: "decimals" },
          { address: t0, abi: erc20Abi, functionName: "balanceOf", args: [v] },
          { address: t1, abi: erc20Abi, functionName: "balanceOf", args: [v] },
          { address: pool, abi: POOL_ABI, functionName: "slot0" },
          { address: pool, abi: POOL_ABI, functionName: "tickSpacing" },
        ],
      });
      const [s0, d0, s1, d1, bal0, bal1, slot0, spacing] = meta;
      const sqrtP = Number(slot0[0]) / 2 ** 96;
      const tick = slot0[1];
      const precio = sqrtP * sqrtP * 10 ** (d0 - d1);

      let rango: { lo: number; hi: number } | null = null;
      let enPosicion = { a0: 0, a1: 0 };
      let pendientes = { a0: 0, a1: 0 };
      let liquidez = 0;
      if (tokenId !== 0n) {
        const pos = await client.readContract({
          address: npm, abi: NPM_ABI, functionName: "positions", args: [tokenId],
        });
        const lo = pos[5], hi = pos[6], L = Number(pos[7]);
        rango = { lo, hi };
        liquidez = L;
        const c = cantidades(sqrtP, 1.0001 ** (lo / 2), 1.0001 ** (hi / 2), L);
        enPosicion = { a0: c.a0 / 10 ** d0, a1: c.a1 / 10 ** d1 };

        // LAS COMISIONES PENDIENTES SE SIMULAN. `tokensOwed0/1` de positions() NO valen: solo se
        // actualizan cuando alguien toca la posicion, asi que en un vault tranquilo se quedan a
        // cero mientras las comisiones se acumulan de verdad.
        try {
          const { result } = await client.simulateContract({
            address: npm, abi: NPM_ABI, functionName: "collect",
            args: [{ tokenId, recipient: v, amount0Max: MAX128, amount1Max: MAX128 }],
            account: v,
          });
          pendientes = { a0: Number(result[0]) / 10 ** d0, a1: Number(result[1]) / 10 ** d1 };
        } catch { /* logica vieja o RPC que no simula: se deja en 0 */ }
      }

      const logicaPublicada = await client.readContract({
        address: factory, abi: keelVaultFactoryAbi, functionName: "rebalancer",
      });
      const bytecode = await client.getCode({ address: v });

      const refEsToken0 = recoverToken0;
      const ref = refEsToken0 ? { symbol: s0, decimals: d0 } : { symbol: s1, decimals: d1 };
      const enRef = (a0: number, a1: number) =>
        refEsToken0 ? a0 + (precio > 0 ? a1 / precio : 0) : a1 + a0 * precio;

      const libre0 = Number(bal0) / 10 ** d0;
      const libre1 = Number(bal1) / 10 ** d1;

      // LA RESERVA CUENTA COMO CAPITAL, Y NO CONTARLA INVENTABA UNA PERDIDA (2026-08-17).
      //
      // `capitalHoy` se compara contra `costBasisRef`, y el contrato METE la reserva ahi dentro:
      // `openPosition` hace `costBasisRef = valorDesplegable + reserve` y `addReserve` la suma
      // tambien. Es capital del dueño, solo que retenido para ir comprando segun cae el precio.
      //
      // Al descontarla de un lado y no del otro, la resta salia sesgada EXACTAMENTE por el importe
      // de la reserva: un vault recien abierto con 45 USDG apartados enseñaba «te falta 44,77 para
      // volver a lo tuyo» y un -4,98% en rojo, cuando no habia perdido nada. El numero era ademas
      // muy convincente, porque coincidia casi al centimo con la reserva.
      //
      // Lo que si se descuenta: la bolsa de gas (no es capital, es combustible) y la ganancia
      // apartada (no es capital, es lo ganado) — `costBasisRef` tampoco las incluye a ellas.
      const retenido0 = (Number(gb0) + Number(profit0)) / 10 ** d0;
      const retenido1 = (Number(gb1) + Number(profit1)) / 10 ** d1;
      const capitalHoy = enRef(
        enPosicion.a0 + Math.max(libre0 - retenido0, 0),
        enPosicion.a1 + Math.max(libre1 - retenido1, 0)
      );
      const objetivo = Number(costBasisRef) / 10 ** ref.decimals;

      return {
        direccion: v, dueno, esMio: esIgual(dueno, address), pool, npm,
        token0: { address: t0, symbol: s0, decimals: d0 },
        token1: { address: t1, symbol: s1, decimals: d1 },
        refEsToken0, ref,
        tokenId, abierto: tokenId !== 0n, rango,
        enRango: !!rango && tick >= rango.lo && tick < rango.hi,
        tick, precio, spacing,
        enPosicion, liquidez, libre: { a0: libre0, a1: libre1 },
        costBasisRef, capitalHoy, objetivo, diferencia: capitalHoy - objetivo,
        profit0, profit1, pendientes,
        gasBudget0: gb0, gasBudget1: gb1, reserva: reserve, reserveInitial,
        halfWidthTicks, ticksBelow, ticksAbove, asimSoportado,
        twapWindow, maxTwapDeviationTicks, swapSlippageBps,
        cooldown, lastRebalance, cuota, pausado: paused, operador, reinvertir: reinvestFees,
        ilRecover, ilRecoverMaxTicks, minSaleOn, minSaleMaxBps,
        recoverMarginBps, colchonSoportado, recoverMarginRef, colchonFijoSoportado,
        reinjectMode, reinjectPctBps, dropStepBps, dropStepsDone,
        rebalancer, impl: implDeClon(bytecode),
        hayActualizacion: !esIgual(rebalancer, logicaPublicada),
        logicaPublicada,
      };
    },
  });
}
